import {
  basketPaymentStatus,
  confirmBasketCheckout,
  confirmCheckout,
  openBasketCheckout,
  openCheckout,
  paymentStatus,
  type CheckoutHandoff,
} from './payments';
import { CheckoutFailure, openRazorpay } from './razorpay';

/**
 * Taking one online checkout from placed booking to paid, end to end.
 *
 * ---------------------------------------------------------------------------
 * A BASKET IS ONE PAYMENT
 * ---------------------------------------------------------------------------
 * `POST /checkout` would place one booking per unit, so five units are five
 * bookings. `payForCheckout` pays for all of them through ONE gateway order
 * and one trip through the Razorpay sheet - and pays BEFORE they exist: the
 * server books the basket only once the money is captured, so closing the
 * sheet leaves nothing behind.
 *
 * `payForBookings` still walks bookings one at a time, and is kept for paying
 * a single booking that was left unpaid, from its own page.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE TREATS AN ERROR AS PROOF OF NON-PAYMENT
 * ---------------------------------------------------------------------------
 * Three different things can fail - minting the order, the sheet itself, and
 * the verify call - and only one of them (the customer pressing back) proves no
 * money moved. Every other failure can happen *after* a successful capture,
 * because Razorpay's webhook reaches the server whether or not this app is
 * still running.
 *
 * So every failure path asks `GET /bookings/:id/payment` before giving up. If
 * the server says `paid`, this moves on. Without that check, a customer whose
 * connection dropped in the last half second is shown a payment failure over
 * money that was taken - and then pays again.
 */

/** Which booking is being paid for, so the screen can count. */
export type PayProgress = {
  /** 1-based. */
  index: number;
  total: number;
  handoff: CheckoutHandoff;
};

export type PayOutcome =
  /**
   * Every booking in the order is paid. `bookingIds` is set for a basket paid
   * as one - its bookings only exist once the payment has landed.
   */
  | { status: 'paid'; bookingIds?: string[] }
  /**
   * The customer backed out of the sheet. Nothing was charged for the booking
   * they were on; `paidCount` earlier ones were.
   *
   * Separated from `failed` because it needs a different sentence: nothing
   * went wrong, and "try again" is an odd thing to say to somebody who just
   * chose not to.
   */
  | { status: 'cancelled'; paidCount: number; total: number }
  | {
      status: 'failed';
      paidCount: number;
      total: number;
      message: string;
      /** True when the money may in fact have moved and we could not confirm. */
      uncertain: boolean;
    };

/** Has the server already recorded this one as paid? Never throws. */
async function alreadyPaid(bookingId: string): Promise<boolean> {
  try {
    const payment = await paymentStatus(bookingId);
    return payment.paymentStatus === 'paid';
  } catch {
    /*
     * Could not ask. Reported as not-paid, which routes to the uncertain
     * failure below rather than to a false success - the one direction that
     * must never be guessed.
     */
    return false;
  }
}

function reasonFrom(thrown: unknown, fallback: string): string {
  return thrown instanceof Error && thrown.message ? thrown.message : fallback;
}

/**
 * Pay for every booking the checkout produced, in order.
 *
 * `onProgress` fires once per booking, just before its sheet opens, so a
 * multi-booking order can show "Payment 2 of 3" rather than reopening what
 * looks like the same sheet twice.
 */
export async function payForBookings(
  bookingIds: string[],
  onProgress?: (progress: PayProgress) => void,
): Promise<PayOutcome> {
  const total = bookingIds.length;
  let paidCount = 0;

  for (const [index, bookingId] of bookingIds.entries()) {
    /*
     * Step 1 - mint the gateway order.
     *
     * A failure here is usually a booking that is no longer payable: already
     * paid (the webhook beat us, or this is a retry after one), or expired out
     * of `awaiting_payment`. Asking the server which tells the two apart -
     * paid is a success to walk past, anything else is a real stop.
     */
    let handoff: CheckoutHandoff;
    try {
      handoff = await openCheckout(bookingId);
    } catch (thrown) {
      if (await alreadyPaid(bookingId)) {
        paidCount += 1;
        continue;
      }
      return {
        status: 'failed',
        paidCount,
        total,
        message: reasonFrom(thrown, 'Could not start the payment.'),
        uncertain: false,
      };
    }

    onProgress?.({ index: index + 1, total, handoff });

    /* Step 2 - the gateway's own sheet. The only step not on our server. */
    let result;
    try {
      result = await openRazorpay(handoff);
    } catch (thrown) {
      const cancelled =
        thrown instanceof CheckoutFailure && thrown.kind === 'cancelled';

      /* Backing out is the one failure that proves nothing was charged, so it
         is the one that does not need asking about. */
      if (cancelled) {
        return { status: 'cancelled', paidCount, total };
      }

      if (await alreadyPaid(bookingId)) {
        paidCount += 1;
        continue;
      }

      const network =
        thrown instanceof CheckoutFailure && thrown.kind === 'network';

      return {
        status: 'failed',
        paidCount,
        total,
        message: reasonFrom(thrown, 'The payment did not go through.'),
        /* A network failure is the case where money may have moved and we
           could not find out. A decline is not. */
        uncertain: network,
      };
    }

    /*
     * Step 3 - hand the outcome to the server, which verifies it against the
     * gateway before believing any of it.
     *
     * A failure here is the least alarming of the three and reads as the most:
     * the payment succeeded, and only our own confirmation call did not land.
     * The webhook completes the booking regardless, which is exactly what
     * `alreadyPaid` is checking for.
     */
    try {
      await confirmCheckout(bookingId, result);
      paidCount += 1;
    } catch (thrown) {
      if (await alreadyPaid(bookingId)) {
        paidCount += 1;
        continue;
      }
      return {
        status: 'failed',
        paidCount,
        total,
        message: reasonFrom(thrown, 'We could not confirm your payment.'),
        /* The gateway said yes. Whatever this is, it is not a decline. */
        uncertain: true,
      };
    }
  }

  return { status: 'paid' };
}

const pause = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * The paid basket's bookings, once the server has made them.
 *
 * Made the moment the capture lands, which is usually before this asks - but
 * the webhook can win the race and still be booking, so it asks a few times
 * before settling for none. Never throws: the payment has succeeded either
 * way, and the bookings list catches up on its next refresh.
 */
async function bookedIds(checkoutId: string): Promise<string[]> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const payment = await basketPaymentStatus(checkoutId);
      if (payment.bookings.length > 0 || payment.status === 'placed') {
        return payment.bookings.map(booking => booking.id);
      }
    } catch {
      // Asked again below.
    }
    await pause(1000);
  }
  return [];
}

/** Has the server already recorded this basket as paid? Never throws. */
async function basketAlreadyPaid(checkoutId: string): Promise<boolean> {
  try {
    const payment = await basketPaymentStatus(checkoutId);
    return payment.paymentStatus === 'paid';
  } catch {
    // Could not ask - reported as not paid, for the same reason as `alreadyPaid`.
    return false;
  }
}

/**
 * Pay for a whole checkout in one go: one gateway order, one sheet, one verify.
 *
 * The same three steps and the same rule as `payForBookings` - every failure
 * other than backing out asks the server before giving up - against the
 * basket's routes instead of a booking's. `total` is how many bookings the
 * basket holds, so a failure can still say how many were paid.
 */
export async function payForCheckout(
  checkoutId: string,
  total: number,
): Promise<PayOutcome> {
  const paid = async (): Promise<PayOutcome> => ({
    status: 'paid',
    bookingIds: await bookedIds(checkoutId),
  });
  const settled = async (): Promise<PayOutcome | null> =>
    (await basketAlreadyPaid(checkoutId)) ? paid() : null;

  let handoff: CheckoutHandoff;
  try {
    handoff = await openBasketCheckout(checkoutId);
  } catch (thrown) {
    return (
      (await settled()) ?? {
        status: 'failed',
        paidCount: 0,
        total,
        message: reasonFrom(thrown, 'Could not start the payment.'),
        uncertain: false,
      }
    );
  }

  let result;
  try {
    result = await openRazorpay(handoff);
  } catch (thrown) {
    if (thrown instanceof CheckoutFailure && thrown.kind === 'cancelled') {
      return { status: 'cancelled', paidCount: 0, total };
    }
    return (
      (await settled()) ?? {
        status: 'failed',
        paidCount: 0,
        total,
        message: reasonFrom(thrown, 'The payment did not go through.'),
        uncertain:
          thrown instanceof CheckoutFailure && thrown.kind === 'network',
      }
    );
  }

  try {
    await confirmBasketCheckout(checkoutId, result);
    return await paid();
  } catch (thrown) {
    return (
      (await settled()) ?? {
        status: 'failed',
        paidCount: 0,
        total,
        message: reasonFrom(thrown, 'We could not confirm your payment.'),
        // The gateway said yes. Whatever this is, it is not a decline.
        uncertain: true,
      }
    );
  }
}

/** How a basket paid straight from the cart ended. */
export type BasketRun<B> =
  /** Paid, and booked by the server. `bookings` may be empty if fetching them lagged. */
  | { status: 'paid'; bookings: B[] }
  /** The customer closed Razorpay. Nothing was booked or charged. */
  | { status: 'cancelled' }
  /** Refused before any money was asked for - a cap, a slot, an address. */
  | { status: 'refused'; message: string }
  | { status: 'failed'; message: string; uncertain: boolean };

/**
 * "Proceed to Pay", end to end, with no screen of our own in between.
 *
 * The basket is placed as a checkout that waits for its money - NOTHING is
 * booked - and Razorpay opens over the cart at once. The server books the
 * basket only when it has itself confirmed the payment with Razorpay (the
 * verify call, or the webhook if the app never gets that far), so a closed
 * sheet, a declined card or a dead phone leaves no booking behind.
 *
 * A basket that costs nothing - coins covered all of it - has nothing to
 * pay; the server books it on placement and it comes back already paid.
 */
export async function payBasket<B>(
  place: () => Promise<{ bookings: B[]; checkoutId: string }>,
  loadBookings: (ids: string[]) => Promise<B[]>,
  units: number,
): Promise<BasketRun<B>> {
  let placed;
  try {
    placed = await place();
  } catch (thrown) {
    return {
      status: 'refused',
      message: reasonFrom(thrown, 'Could not start the payment.'),
    };
  }

  if (placed.bookings.length > 0) {
    return { status: 'paid', bookings: placed.bookings };
  }

  const outcome = await payForCheckout(placed.checkoutId, units);

  if (outcome.status === 'paid') {
    const ids = outcome.bookingIds ?? [];
    const bookings =
      ids.length > 0 ? await loadBookings(ids).catch((): B[] => []) : [];
    return { status: 'paid', bookings };
  }
  if (outcome.status === 'cancelled') return { status: 'cancelled' };
  return {
    status: 'failed',
    message: outcome.message,
    uncertain: outcome.uncertain,
  };
}
